import { assertNever } from "@@/shared/assert-never";
import { hejalfredScope } from "@@/shared/scopes";
import { parseScope } from "./parse-scope";
import type { Permission} from "./permission";
import { permissions } from "./permission";
import type { Scope } from "./scope";

const permissionCheck = (
    hasPermission: Permission,
    requiredPermission: Permission
) => {
    // If users permission doesn't is not a real permission; return false
    if (!permissions.includes(hasPermission)) return false;

    switch (hasPermission) {
        case "owner":
            // owner has access to everything
            return true;
        case "write":
            // user has `write` access, that means access to anything
            // as long as `owner` permission is not required.
            return requiredPermission !== "owner";
        case "read":
            // user has `read` access, that means access to everything
            // where read only access is allowed
            return requiredPermission === "read";
        default:
            assertNever(hasPermission);
    }
};

const getPermissions = (permission: Permission) => {
    switch (permission) {
        case "owner":
            return ["owner", "write", "read"];
        case "write":
            return ["write", "read"];
        case "read":
            return ["read"];
        default:
            assertNever(permission);
    }
};

const hasPermission = (
    userScopes: Scope[],
    resource: string | undefined,
    requiredPermission: Permission
): boolean => {
    if (userScopes.includes(hejalfredScope)) return true;
    if (typeof resource === "undefined") return false;
    if (!resource.trim())
        throw new Error("Invalid resource, resource name/id must not be empty");
    const _hasPermission =
        !!userScopes.length &&
        userScopes.some(userScope => {
            const scope = parseScope(userScope);
            return (
                scope.segments.length === 1 &&
                scope.segments[0] === resource &&
                permissionCheck(scope.permission, requiredPermission)
            );
        });
    return _hasPermission;
};

export { permissionCheck, getPermissions, hasPermission };
